import { IActivityHistoryGroup } from "../../domain/model";

// Helper: obtener la fecha más reciente de una lista de items
const getMostRecentDate = (items: { date: string }[]): Date => {
  return items.reduce((latest, item) => {
    const itemDate = new Date(item.date);
    return itemDate > latest ? itemDate : latest;
  }, new Date(0));
};

// Obtener la fecha más reciente de un subgrupo
const getSubGroupMostRecentDate = (subGroup: { items: { date: string }[] }): Date => {
  return getMostRecentDate(subGroup.items);
};

/**
 * Fecha por la que un grupo queda posicionado en la lista: la más reciente
 * considerando TANTO sus items planos COMO sus subgrupos (diagnósticos).
 *
 * IMPORTANTE: esta es la misma clave que usa `sortGroupsByMostRecentDate` para
 * ordenar. Cualquier funcionalidad que mapee una fecha a la posición de un grupo
 * (p. ej. "Anar a data") DEBE usar esta función y no `group.items[0].date`, que
 * puede ser más antigua que el subgrupo y romper la monotonía del orden.
 */
export const getGroupMostRecentDate = (group: IActivityHistoryGroup): Date => {
  const itemDates = group.items.length > 0 ? [getMostRecentDate(group.items)] : [];
  const subGroupDates = group.subGroups.map(getSubGroupMostRecentDate);
  const allDates = [...itemDates, ...subGroupDates];
  return allDates.length > 0 ? new Date(Math.max(...allDates.map((d) => d.getTime()))) : new Date(0);
};

export const sortGroupsByMostRecentDate = (arr: IActivityHistoryGroup[]) => {
  // Ordenar items de más reciente a más antiguo
  const sortItemsByDate = (items: { date: string }[]) => {
    return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  };

  // Procesar cada grupo
  arr.forEach((group) => {
    // Ordenar items del grupo
    group.items = sortItemsByDate(group.items);

    // Ordenar items de cada subgrupo
    group.subGroups.forEach((subGroup) => {
      subGroup.items = sortItemsByDate(subGroup.items);
    });

    // Ordenar subgrupos según el item más reciente en cada uno
    group.subGroups.sort((a, b) => getSubGroupMostRecentDate(b).getTime() - getSubGroupMostRecentDate(a).getTime());
  });

  // Finalmente, ordenar los grupos por su fecha más reciente
  return arr.sort((a, b) => getGroupMostRecentDate(b).getTime() - getGroupMostRecentDate(a).getTime());
};
